// Google's map with our drawing on it (BL-018, package 4): the map in Blueprint's style, and an
// OverlayView whose box covers the map's own, so GoogleMap draws the same SVG on it as on the stand-in,
// from the map's projection. The map is styled by its style array (no Map ID, no Advanced Markers),
// and restyled in place when the page's theme changes.
//
// Our pins never drift off their place (QA-761). The overlay's box moves at once in draw(), on Google's
// frame, but React lays the SVG out for a new projection a render later; so the box stays on the frame
// the drawing on screen was laid out for (its corners' places, and its scale), re-placed from the map's
// projection now, until GoogleMap says the drawing for the new view is in (drawn), in the same frame.
// Through a drag the box rides the pane; through a zoom it scales round its corner as the map does.
//
// Dragging the map moves the map, never the page: every gesture on it is the map's ('greedy'), and
// GoogleMap's frame keeps touches and overscroll to itself (base.css). The pins are ours, so Google's
// own places aren't clickable on it.
import type { MapsApi, MapsListener, MapsMap, MapsOverlayView, MapsProjection } from './map-loader.ts';
import { mapStyle, type MapTheme } from './map-style.ts';
import { FIT_MAX_ZOOM, FIT_PAD, type Project } from './map-view.ts';

/** How the map is made: only Google's zoom buttons of its controls, no clickable places, every gesture the map's. */
export const MAP_OPTIONS = {
  disableDefaultUI: true,
  zoomControl: true,
  clickableIcons: false,
  keyboardShortcuts: true,
  gestureHandling: 'greedy',
  isFractionalZoomEnabled: true,
} as const;

/**
 * The frame a drawing is laid out in: where the box's top left and bottom right corners were on the
 * map, and its size, in pixels.
 */
export interface MapFrame {
  topLeft: { lat: number; lng: number };
  bottomRight: { lat: number; lng: number };
  width: number;
  height: number;
}

/** What our drawing needs from the map each time it moves: the projection into its box, and the box. */
export interface MapView {
  project: Project;
  width: number;
  height: number;
  zoom: number;
  /** The frame `project` lays the drawing out in: hand it back to drawn() once that drawing is in. */
  frame: MapFrame;
}

/** The overlay's box, which our SVG goes in: an element the page made (a div), placed by the overlay. */
export interface OverlayBox {
  style: {
    left: string;
    top: string;
    width: string;
    height: string;
    position: string;
    transform: string;
    transformOrigin: string;
  };
}

/**
 * Where the box goes so a drawing laid out in `frame` sits on its places now: its top left corner at
 * where the frame's top left is now (in the pane's pixels, `divOf`), scaled round it by how much the map
 * has zoomed since (Web Mercator is the same scale both ways, so the width says it). A pin the frame
 * put at (x, y) is then at left + scale × x, top + scale × y: where `divOf` puts its place.
 */
export function boxOnFrame(
  frame: MapFrame,
  divOf: (p: { lat: number; lng: number }) => { x: number; y: number } | null,
): { left: number; top: number; scale: number } | null {
  const a = divOf(frame.topLeft);
  const b = divOf(frame.bottomRight);
  if (!a || !b || !frame.width) return null;
  const scale = (b.x - a.x) / frame.width;
  return Number.isFinite(scale) && scale > 0 ? { left: a.x, top: a.y, scale } : null;
}

export interface LiveMap {
  /** Fits these points (a single one is shown close in; none, the centre). */
  fit(points: readonly { lat: number; lng: number }[], center?: { lat: number; lng: number } | null): void;
  /** Restyles the map for light or dark, in place. */
  setTheme(theme: MapTheme): void;
  /**
   * The drawing for this view is in (GoogleMap's layout effect, before the browser paints): the box
   * moves onto its frame, from where the map is now.
   */
  drawn(v: MapView): void;
  /** Takes the overlay off and stops listening. */
  destroy(): void;
}

export function liveMap(
  api: MapsApi,
  host: { clientWidth: number; clientHeight: number },
  box: OverlayBox,
  o: {
    theme: MapTheme;
    points: readonly { lat: number; lng: number }[];
    center?: { lat: number; lng: number } | null;
    onView: (v: MapView) => void;
  },
): LiveMap {
  const map: MapsMap = new api.Map(host, {
    ...MAP_OPTIONS,
    styles: mapStyle(o.theme),
    center: o.center ?? { lat: 50, lng: -100 },
    zoom: o.center ? FIT_MAX_ZOOM - 1 : 3,
  });
  let theme = o.theme;
  const listeners: MapsListener[] = [];
  // the frame of the drawing on screen: the box keeps to it until the next one is in
  let shown: MapFrame | null = null;
  const place = (pr: MapsProjection | null) => {
    if (!shown || !pr) return;
    const at = boxOnFrame(shown, (p) => pr.fromLatLngToDivPixel(new api.LatLng(p.lat, p.lng)));
    if (!at) return;
    Object.assign(box.style, {
      left: `${at.left}px`,
      top: `${at.top}px`,
      width: `${shown.width}px`,
      height: `${shown.height}px`,
      // the same scale is no transform: a drag only moves it
      transform: Math.abs(at.scale - 1) < 1e-6 ? '' : `scale(${at.scale})`,
      transformOrigin: '0 0',
    });
  };

  class Ours extends api.OverlayView {
    override onAdd() {
      box.style.position = 'absolute';
      this.getPanes()?.overlayMouseTarget.append(box);
    }
    override draw() {
      const pr = this.getProjection();
      const width = host.clientWidth;
      const height = host.clientHeight;
      if (!pr || !width || !height) return;
      // the next drawing's frame is the map's own box: its corners, where they are on the map now
      const corner = pr.fromContainerPixelToLatLng(new api.Point(0, 0));
      const far = pr.fromContainerPixelToLatLng(new api.Point(width, height));
      if (!corner || !far) return;
      const frame: MapFrame = {
        topLeft: { lat: corner.lat(), lng: corner.lng() },
        bottomRight: { lat: far.lat(), lng: far.lng() },
        width,
        height,
      };
      // the drawing on screen stays on its places until the next one is in
      place(pr);
      // a point's place in this frame, however the map has moved by the time React lays it out: Google's
      // projection is the map's as it is now, so it's read back through the frame's corners
      const divOf = (p: { lat: number; lng: number }) =>
        pr.fromLatLngToDivPixel(new api.LatLng(p.lat, p.lng));
      const project: Project = (lat, lng) => {
        const at = boxOnFrame(frame, divOf);
        const p = divOf({ lat, lng });
        return at && p ? [(p.x - at.left) / at.scale, (p.y - at.top) / at.scale] : [NaN, NaN];
      };
      onView({ project, width, height, zoom: map.getZoom() ?? 0, frame });
    }
    override onRemove() {
      (box as unknown as { remove?: () => void }).remove?.();
    }
  }
  const onView = o.onView;
  const overlay: MapsOverlayView = new Ours();
  overlay.setMap(map);
  // a pan moves the pane with the map; once it settles, the drawing is laid out again for what shows
  listeners.push(map.addListener('idle', () => overlay.draw()));

  const live: LiveMap = {
    fit(points, center) {
      if (points.length > 1) {
        const b = new api.LatLngBounds();
        for (const p of points) b.extend(p);
        map.fitBounds(b, FIT_PAD);
        // a fit never comes closer than a street or two
        const once = map.addListener('idle', () => {
          once.remove();
          listeners.splice(listeners.indexOf(once), 1);
          if ((map.getZoom() ?? 0) > FIT_MAX_ZOOM) map.setZoom(FIT_MAX_ZOOM);
        });
        listeners.push(once);
      } else {
        const p = points[0] ?? center;
        if (!p) return;
        map.setCenter(p);
        map.setZoom(FIT_MAX_ZOOM - 1);
      }
    },
    drawn(v) {
      shown = v.frame;
      place(overlay.getProjection());
    },
    setTheme(next) {
      if (next === theme) return;
      theme = next;
      map.setOptions({ styles: mapStyle(next) });
    },
    destroy() {
      for (const l of listeners) l.remove();
      listeners.length = 0;
      overlay.setMap(null);
    },
  };
  live.fit(o.points, o.center);
  return live;
}
